'use client';

import { cn } from '@/lib/utils';

export interface SwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  label?: string;
  description?: string;
  id?: string;
  className?: string;
  size?: 'sm' | 'md';
}

/** Switch with its label and description wired for assistive technology. */
export function Switch({
  checked,
  onCheckedChange,
  disabled = false,
  label,
  description,
  id,
  className,
  size = 'md',
}: SwitchProps) {
  const labelId = id && label ? `${id}-label` : undefined;
  const descriptionId = id && description ? `${id}-description` : undefined;

  const control = (
    <span className="flex size-11 shrink-0 items-center justify-center lg:size-auto">
      <button
        type="button"
        role="switch"
        id={id}
        aria-checked={checked}
        aria-labelledby={labelId}
        aria-describedby={descriptionId}
        aria-label={!labelId ? label : undefined}
        disabled={disabled}
        onClick={() => !disabled && onCheckedChange(!checked)}
        className={cn(
          'relative inline-flex shrink-0 items-center rounded-full transition-colors duration-150',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-canvas',
          'disabled:cursor-not-allowed disabled:opacity-50',
          size === 'sm' ? 'h-5 w-9' : 'h-6 w-11',
          checked ? 'bg-accent' : 'bg-line-strong'
        )}
      >
        <span
          className={cn(
            'inline-block rounded-full bg-white shadow-[0_1px_2px_rgb(0_0_0/0.3)] transition-transform duration-150',
            size === 'sm' ? 'size-4' : 'size-5',
            checked
              ? size === 'sm'
                ? 'translate-x-[18px]'
                : 'translate-x-[22px]'
              : 'translate-x-0.5'
          )}
        />
      </button>
    </span>
  );

  if (!label && !description) return <span className={className}>{control}</span>;

  return (
    <div className={cn('flex items-start justify-between gap-4', className)}>
      <div className="min-w-0 flex-1">
        {label ? (
          <span id={labelId} className="text-sm font-medium text-ink">
            {label}
          </span>
        ) : null}
        {description ? (
          <p id={descriptionId} className="mt-0.5 text-[13px] text-ink-2">
            {description}
          </p>
        ) : null}
      </div>
      {control}
    </div>
  );
}
